import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { SearchBox } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/SearchBox';
import { Typography } from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';

import locator from 'locator';
import { navigateTo } from 'renderer/utils/navigate';
import { useSearchParams } from 'renderer/hooks/useSearchParams';
import { queryToFilters } from 'renderer/utils/queryToFilters';
import {
  RECIPE_QUERY_PAGE,
  RECIPE_QUERY_SEARCH_TERM,
  RECIPE_QUERY_SORT_BY,
  RECIPE_SEARCH_ID,
  RECIPE_SEARCH_PATH,
} from '../constants';
import { recipesSummaryCacheKey } from '../utils/recipesSummaryCacheKey';

import styles from './RecipeSearchBar.module.scss';

const defaultHeadingCopy = 'Ready for your next recipe? Please enter a new search term';

const getHeading = (searchTerm: string, searchResultCount: number | null) => {
  if (!searchTerm) return defaultHeadingCopy;

  return (
    <>
      Showing {searchResultCount} results for{' '}
      <Typography element="span" styleAs="sectionHeading">
        {searchTerm}
      </Typography>
    </>
  );
};

const RecipeSearchBar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState('');

  const isSearchPage = locator.pathname === RECIPE_SEARCH_PATH;

  const searchTerm = searchParams.get(RECIPE_QUERY_SEARCH_TERM) ?? '';
  const sortBy = searchParams.get(RECIPE_QUERY_SORT_BY) ?? '';
  const pageNumber = Number(searchParams.get(RECIPE_QUERY_PAGE) ?? 1) || 1;
  const filtersParam = queryToFilters(searchParams);

  const { data } = useSWR(
    recipesSummaryCacheKey(searchTerm, sortBy, pageNumber, filtersParam),
    null,
  );

  const searchResultCount = data?.totalMatches ?? null;

  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  const handleSubmit = () => {
    if (isSearchPage) {
      setSearchParams({ [RECIPE_QUERY_SEARCH_TERM]: inputValue }, { replace: true });
    } else {
      navigateTo(
        `${RECIPE_SEARCH_PATH}?${RECIPE_QUERY_SEARCH_TERM}=${encodeURIComponent(inputValue)}`,
      );
    }
  };

  return (
    <div className={styles.container}>
      {isSearchPage && (
        <Typography element="h1" styleAs="sectionHeadingLight">
          {getHeading(searchTerm, searchResultCount)}
        </Typography>
      )}
      <SearchBox
        className={styles.searchBox}
        ariaTitle="Recipes"
        id={RECIPE_SEARCH_ID}
        value={inputValue}
        placeholder="Search by recipe or ingredient..."
        onChange={e => setInputValue(e.target.value)}
        onSubmit={handleSubmit}
        onClearClick={() => {
          setInputValue('');
        }}
      />
    </div>
  );
};

export default RecipeSearchBar;
